/**
 * RELATÓRIO DE RECEITAS E DESPESAS (P3, fase 4) — a tabela por período para o
 * contador e o imposto de renda: data, descrição, valor, documento e situação.
 * Exporta em Excel (aba das linhas e aba dos totais por mês).
 *
 * NÃO é livro-caixa nem LCDPR (decisão do gestor). As contas moram em
 * src/dominio/relatorio.ts (testadas em testes/relatorio.mjs); esta tela só
 * escolhe o período, filtra e mostra.
 *
 * Quem vê: gestor e Consulta. O comprador não — o relatório tem as comissões.
 * Tudo sai do que já está neste aparelho (fichas, pagamentos, extrato, catira
 * e notas): funciona sem sinal, e o que outro aparelho lançou entra depois do
 * «Sincronizar».
 *
 * R2B (revisão de 06/10/2026): dois totais que NÃO se somam — «Realizado
 * (caixa)», o dinheiro que mudou de mão, e «Previsto», o que falta pagar ou
 * receber. Antes o total misturava os dois. O Documento mostra a nota curta
 * («NF-e 4567»); a chave inteira fica na planilha.
 */
import { useMemo, useState, type CSSProperties } from 'react'
import { baixar } from '../dados/arquivos'
import { usarEstado } from '../dados/armazem'
import { usarCatira } from '../dados/catira'
import { usarExtrato } from '../dados/extrato'
import { usarNotas } from '../dados/notas'
import { usarPagamentos } from '../dados/pagamentos'
import type { Eu } from '../dados/sessao'
import { dataBR, hoje, reais } from '../dominio/numeros'
import { montarXlsx, nomeDeArquivo } from '../dominio/planilha'
import {
  abasDoRelatorio, linhasDoRelatorio, NOTA_DO_RODAPE, nomeDoMes, textoDaSituacao, totaisDoRelatorio,
  type Lado, type LinhaDoRelatorio, type TipoDaLinha,
} from '../dominio/relatorio'
import { avisar, Campo, Dica, Segmento, type Ir } from '../ui'

const f2 = (n: number) => String(n).padStart(2, '0')
const iso = (d: Date) => `${d.getFullYear()}-${f2(d.getMonth() + 1)}-${f2(d.getDate())}`

function esteMes(): [string, string] {
  const [a, m] = hoje().split('-').map(Number) as [number, number]
  return [iso(new Date(a, m - 1, 1)), iso(new Date(a, m, 0))]
}
function doAno(deslocar = 0): [string, string] {
  const a = Number(hoje().slice(0, 4)) + deslocar
  return [`${a}-01-01`, `${a}-12-31`]
}

type Filtro = 'tudo' | TipoDaLinha

export function Relatorio({ eu, ir }: { eu: Eu; ir: Ir }) {
  const { fichas } = usarEstado()
  const pg = usarPagamentos()
  const ext = usarExtrato()
  const cat = usarCatira()
  const nt = usarNotas()
  // o relatório abre no ano: é o recorte do imposto de renda
  const [periodo, setPeriodo] = useState<[string, string]>(() => doAno())
  const [de, ate] = periodo
  const [filtro, setFiltro] = useState<Filtro>('tudo')

  const todas = useMemo(() => linhasDoRelatorio({
    fichas: fichas.filter((f) => f.situacao !== 'excluida'),
    quitacoes: pg.quitacoes, comissoes: pg.comissoes, comissoesCliente: pg.comissoesCliente,
    // 3F: a venda da revenda entra pelos recebimentos (o que falta, «a receber»)
    vendas: pg.vendas, recebimentos: pg.recebimentos,
    // R2B: o que o cliente pagou da comissão e a comissão paga fora do acerto (014)
    recebimentosComissao: pg.recebimentosComissao, pagamentosComissao: pg.pagamentosComissao,
    lancamentos: ext.lancamentos, acertos: ext.acertos, movimentos: cat.movimentos, notas: nt.notas,
  }, de, ate), [fichas, pg, ext, cat.movimentos, nt.notas, de, ate])
  const linhas = filtro === 'tudo' ? todas : todas.filter((l) => l.tipo === filtro)
  const t = totaisDoRelatorio(linhas)

  // o comprador não chega aqui (o botão não aparece), mas a tela confere de novo
  if (eu.pessoa.papel === 'comprador') return <div className="cartao vazio">O relatório é só do gestor e da Consulta.</div>

  function exportar() {
    if (!linhas.length) { window.alert('Não há nada neste período para exportar.'); return }
    const qual = filtro === 'receita' ? 'receitas' : filtro === 'despesa' ? 'despesas' : 'receitas-e-despesas'
    baixar(montarXlsx(abasDoRelatorio(linhas, de, ate, eu.conta.nome_fantasia)), `${nomeDeArquivo(qual, de, 'a', ate)}.xlsx`,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    avisar('Planilha salva em Downloads')
  }

  const grande: CSSProperties = { fontSize: '1.35rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }

  return (
    <div>
      <h2 className="secao">Receitas e despesas</h2>
      <Dica>Tabela para o <b>contador</b> e o <b>imposto de renda</b>, com o que já está neste aparelho. Não é livro-caixa nem LCDPR.
        Sincronize antes, para entrar o que foi lançado em outros aparelhos.</Dica>

      <div className="cartao">
        <div className="linha">
          <button className="mini" onClick={() => setPeriodo(doAno())}>Este ano</button>
          <button className="mini" onClick={() => setPeriodo(doAno(-1))}>Ano passado</button>
          <button className="mini" onClick={() => setPeriodo(esteMes())}>Este mês</button>
        </div>
        <div className="linha" style={{ marginTop: 8 }}>
          <Campo rotulo="De" tipo="date" valor={de} mudar={(v) => v && setPeriodo([v, ate])} />
          <Campo rotulo="Até" tipo="date" valor={ate} mudar={(v) => v && setPeriodo([de, v])} />
        </div>
        <div style={{ marginTop: 8 }}>
          <Segmento<Filtro> valor={filtro} mudar={setFiltro} opcoes={[['tudo', 'Tudo'], ['receita', 'Receitas'], ['despesa', 'Despesas']]} />
        </div>
      </div>

      <div className="resultado">
        <div className="mini-dica" style={{ marginTop: 0 }}>De {dataBR(de)} a {dataBR(ate)} · {t.periodo.linhas} linha{t.periodo.linhas === 1 ? '' : 's'}</div>
        {/* R2B: dois totais, nunca somados */}
        <QuadroDoTotal titulo="Realizado (caixa)" dica="pago e recebido no período" lado={t.periodo.realizado} filtro={filtro} grande={grande} />
        <QuadroDoTotal titulo="Previsto" dica="a pagar, a receber e sem data de pagamento" lado={t.periodo.previsto} filtro={filtro} grande={grande} />
      </div>

      <button className="botao" onClick={exportar}>Exportar em Excel</button>

      {t.meses.length > 1 && (
        <>
          <h2 className="secao">Por mês</h2>
          <div className="cartao" style={{ overflowX: 'auto' }}>
            <table style={tabela}>
              <thead><tr><th style={th}>Mês</th>
                {filtro !== 'despesa' && <th style={thN}>Receitas<br />realizado · previsto</th>}
                {filtro !== 'receita' && <th style={thN}>Despesas<br />realizado · previsto</th>}
                {filtro === 'tudo' && <th style={thN}>Saldo realizado</th>}</tr></thead>
              <tbody>
                {t.meses.map((m) => (
                  <tr key={m.mes}><td style={td}>{nomeDoMes(m.mes)}</td>
                    {filtro !== 'despesa' && <td style={tdN}>{reais(m.realizado.receitas)}<div className="mini-dica" style={{ marginTop: 0 }}>{reais(m.previsto.receitas)}</div></td>}
                    {filtro !== 'receita' && <td style={tdN}>{reais(m.realizado.despesas)}<div className="mini-dica" style={{ marginTop: 0 }}>{reais(m.previsto.despesas)}</div></td>}
                    {filtro === 'tudo' && <td style={tdN}>{reais(m.realizado.saldo)}</td>}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2 className="secao">Linhas</h2>
      {linhas.length ? (
        <div className="cartao" style={{ overflowX: 'auto' }}>
          <table style={tabela}>
            <thead><tr><th style={th}>Data</th><th style={th}>Descrição e documento</th><th style={thN}>Valor e situação</th></tr></thead>
            <tbody>{linhas.map((l) => <Linha key={l.id} l={l} ir={ir} />)}</tbody>
          </table>
        </div>
      ) : <div className="cartao vazio">Nada neste período.</div>}

      <div className="cartao">
        <div className="mini-dica" style={{ marginTop: 0 }}><b>O que entra e o que não entra</b></div>
        {NOTA_DO_RODAPE.map((s, i) => <div className="mini-dica" key={i}>{s}</div>)}
      </div>
    </div>
  )
}

/** R2B · Um dos dois totais (realizado ou previsto): receitas, despesas e saldo. */
function QuadroDoTotal({ titulo, dica, lado, filtro, grande }: { titulo: string; dica: string; lado: Lado; filtro: Filtro; grande: CSSProperties }) {
  return (
    <div style={{ borderTop: '1px solid var(--linha)', marginTop: 8, paddingTop: 6 }}>
      <div className="tl"><span><b>{titulo}</b> <span className="mini-dica">— {dica}</span></span></div>
      <div className="linha" style={{ textAlign: 'center', marginTop: 4 }}>
        {filtro !== 'despesa' && <div><div style={{ ...grande, color: 'var(--verde)' }}>{reais(lado.receitas)}</div><div className="mini-dica">receitas</div></div>}
        {filtro !== 'receita' && <div><div style={{ ...grande, color: 'var(--vermelho)' }}>{reais(lado.despesas)}</div><div className="mini-dica">despesas</div></div>}
      </div>
      {filtro === 'tudo' && <div className="tl"><span>Saldo (receitas − despesas)</span><b>{reais(lado.saldo)}</b></div>}
    </div>
  )
}

function Linha({ l, ir }: { l: LinhaDoRelatorio; ir: Ir }) {
  const fichaId = l.fichaId
  const receita = l.tipo === 'receita'
  return (
    <tr>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dataBR(l.data)}</td>
      <td style={td}>
        <div><b>{l.categoria}</b> · {l.descricao}</div>
        {l.documento && (
          <div className="mini-dica" style={{ marginTop: 2, overflowWrap: 'anywhere' }}>
            {fichaId
              ? <span className="clicavel" style={{ textDecoration: 'underline' }} onClick={() => ir({ tela: 'ficha', id: fichaId, aba: 'pagamento' })}>{l.documento}</span>
              : l.documento}
          </div>
        )}
      </td>
      {/* valor e situação na mesma célula: no celular, quatro colunas não cabem */}
      <td style={tdN}>
        <div style={{ color: receita ? 'var(--verde)' : 'var(--vermelho)', fontWeight: 700 }}>{receita ? '' : '− '}{reais(l.valor)}</div>
        <span className={`etiqueta ${l.situacao === 'pago' ? 'ok' : 'aberta'}`}
          style={{ whiteSpace: 'normal', display: 'inline-block', marginTop: 4, maxWidth: 130, textAlign: 'right' }}>{textoDaSituacao(l)}</span>
      </td>
    </tr>
  )
}

/* tabela simples, no tema do app (o estilo.css não tem tabela fora do papel) */
const tabela = { width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' } as const
const th = { textAlign: 'left', padding: '6px 6px', borderBottom: '1px solid var(--linha)', color: 'var(--apagado)', fontSize: '.72rem', textTransform: 'uppercase' } as const
const thN = { ...th, textAlign: 'right' } as const
const td = { padding: '8px 6px', borderBottom: '1px solid var(--linha)', verticalAlign: 'top' } as const
const tdN = { ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const
